import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import { useRef, useCallback, useEffect, useState } from 'react';
import { useId } from '@reach/auto-id';
import { useOnClickOutside } from "../../hooks/useOnClickOutside";
import { useControlledComponent } from "../../hooks/useControlledComponent";
import { useClickTarget } from "./useClickTarget";
export function usePopover() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      providedTargetEl = _ref.targetEl,
      isShowingProp = _ref.isShowing,
      onRequestChange = _ref.onRequestChange,
      defaultShowing = _ref.defaultShowing,
      initialFocusRef = _ref.initialFocusRef,
      _ref$shouldFocusOnMou = _ref.shouldFocusOnMount,
      shouldFocusOnMount = _ref$shouldFocusOnMou === void 0 ? true : _ref$shouldFocusOnMou,
      _ref$shouldCloseOnEsc = _ref.shouldCloseOnEscape,
      shouldCloseOnEscape = _ref$shouldCloseOnEsc === void 0 ? true : _ref$shouldCloseOnEsc;

  var id = useId(); // track refs

  var _useState = useState(providedTargetEl),
      _useState2 = _slicedToArray(_useState, 2),
      targetEl = _useState2[0],
      setTargetEl = _useState2[1];

  var triggerRef = useRef(null);
  var setTargetRef = useCallback(function (el) {
    triggerRef.current = el;

    if (typeof providedTargetEl === 'undefined') {
      setTargetEl(el);
    }
  }, [providedTargetEl]);
  var popoverRef = useRef(null);
  useEffect(function () {
    if (providedTargetEl) {
      setTargetEl(providedTargetEl);
    }
  }, [setTargetEl, providedTargetEl]); // popover can be uncontrolled or controlled. This abstraction
  // handles both

  var _useControlledCompone = useControlledComponent(isShowingProp, onRequestChange, defaultShowing),
      _useControlledCompone2 = _slicedToArray(_useControlledCompone, 2),
      isShowing = _useControlledCompone2[0],
      setIsShowing = _useControlledCompone2[1];
  /**
   * Trigger showing a popover
   */


  var showPopover = useCallback(function () {
    setIsShowing(true);
  }, [setIsShowing]);
  /**
   * Trigger hiding a popover
   */

  var hidePopover = useCallback(function () {
    setIsShowing(false);

    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [setIsShowing]);
  /**
   * Get required click target binding props.
   */

  var targetBindings = useClickTarget({
    id: id,
    isShowing: isShowing,
    show: showPopover,
    hide: hidePopover
  });
  /**
   * Close a popover when clicking outside of it if
   * in "click" mode.
   */

  var _useOnClickOutside = useOnClickOutside(hidePopover, {
    enabled: isShowing
  }),
      bindOnClickOutside = _useOnClickOutside.bind;
  /**
   * Handle focus when showing
   */


  useEffect(function () {
    // if rendering the popover, focus the specified element
    if (shouldFocusOnMount && isShowing) {
      window.requestAnimationFrame(function () {
        if (initialFocusRef && initialFocusRef.current) {
          initialFocusRef.current.focus();
        } else if (popoverRef.current) {
          popoverRef.current.focus();
        }
      });
    }
  }, [shouldFocusOnMount, initialFocusRef, isShowing, popoverRef]);
  /**
   * Potentially close the popover and return focus
   * back to the target when escape is pressed.
   */

  var handleKeyDown = useCallback(function (e) {
    if (shouldCloseOnEscape && e.key === 'Escape') {
      e.stopPropagation();
      hidePopover();
    }
  }, [shouldCloseOnEscape, hidePopover]);

  var targetProps = _objectSpread(_objectSpread({
    ref: setTargetRef
  }, bindOnClickOutside), targetBindings);

  var contentProps = _objectSpread({
    isShowing: isShowing,
    target: targetEl,
    shouldUsePositioner: true,
    id: id,
    role: 'dialog',
    'aria-modal': false,
    ref: popoverRef,
    onKeyDown: handleKeyDown
  }, bindOnClickOutside);

  return {
    id: id,
    hidePopover: hidePopover,
    showPopover: showPopover,
    isShowing: isShowing,
    targetEl: targetEl,
    bindContent: contentProps,
    bindTarget: targetProps
  };
}